import React, { useState, useEffect, useRef } from "react";
import EmailChange from "./models/EmailChange";
import PasswordChange from "./models/PasswordChange";
import ChangePhone from "./models/ChangePhone";
import { FormattedMessage } from "react-intl";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { getCorporate } from "sr/utils/api/corporate/getCorporate";
import ContentWithLoader from "sr/layout/common/ContentLoader";
import { useAuth } from "app/pages/module/auth";
import { sendOTP } from "sr/utils/api/sendOTP";
import { Input } from "sr/helpers";
import { verifyOTP } from "sr/utils/api/verifyOTP";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faEnvelope,
  faLock,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { ModelWrapperWithButton } from "../../layout/ModelWrapperWithButton";
import { RequirementCommonHeader } from "../../layout/RequirementCommonHeader";

export const CorporateAccountSettings = () => {
  const [showModal, setShowModal] = useState(false);
  const [corporateId, setCorporateId] = useState<string>();
  const [activeComponent, setActiveComponent] = useState<any>({});
  const [data, setData] = useState<{ corporate?: any; user?: any }>({});
  const [loader, setLoader] = useState(true);
  const { logout } = useAuth();
  const [otpsend, setOtpSend] = useState<string>("");
  const [otp, setOTP] = useState<string>("");

  const HandleModel = (data: any) => setShowModal(data);

  useEffect(() => {
    const getdetails = getUserDetails();
    setCorporateId(getdetails.user_id);
  }, []);

  const fetchData = async () => {
    setLoader(true);
    try {
      const result = await getCorporate(corporateId as string);
      if (result) {
        setData({
          corporate: result?.data?.corporate,
          user: result?.data?.user,
        });
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (corporateId) {
      fetchData();
    }
  }, [corporateId]);

  const childRef = useRef(null);

const HandleChange = async () => {
  if (childRef.current && (childRef.current as any).submitForm) {
    await (childRef.current as any).submitForm();
  }
  fetchData();
};

  const AllModels = [
    {
      label: "EmailChange",
      componentName: EmailChange,
      headertitle: (
        <FormattedMessage
          id="CORPORATE.ACCOUNT.SETTINGS.CHANGE_EMAIL.POPUP.TITLE"
          defaultMessage="Change email"
        />
      ),
      icon: faEnvelope,
    },
    {
      label: "PasswordChange",
      componentName: PasswordChange,
      headertitle: (
        <FormattedMessage
          id="CORPORATE.ACCOUNT.SETTINGS.CHANGE_PASSWORD.POPUP.TITLE"
          defaultMessage="Change password"
        />
      ),
      icon: faLock,
    },
    {
      label: "ChangePhone",
      componentName: ChangePhone,
      headertitle: (
        <FormattedMessage
          id="CORPORATE.ACCOUNT.SETTINGS.CHANGE_PHONENO.POPUP.TITLE"
          defaultMessage="Change Phone Number"
        />
      ),
      icon: faPhone,
    },
  ];

  const openModel = (index: number) => {
    const activeComp = AllModels[index];
    setActiveComponent(activeComp);
    setShowModal(true);
  };

  const handleOTP = async (type: "email" | "sms") => {
    setLoader(true);

    const payload: any = {
      username: data?.user?.username,
      channel: type,
    };

    if (type === "email") {
      payload.email = data?.corporate?.email;
    } else {
      payload.phone_number = data?.corporate?.mobile_number;
    }

    try {
      const otpresult = await sendOTP(payload);
      setLoader(false);

      if (otpresult?.success || otpresult?.status) {
        setOtpSend(type === "email" ? "Email" : "PhoneNo");
      } else {
        console.error("OTP not sent:", otpresult);
      }
    } catch (e) {
      setLoader(false);
      console.error("OTP error:", e);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const payload: any = {
        username: data?.user?.username,
        otp,
        channel: otpsend === "Email" ? "email" : "sms",
      };

      if (otpsend === "Email") {
        payload.email = data?.corporate?.email;
      } else {
        payload.phone_number = data?.corporate?.mobile_number;
      }

      const res = await verifyOTP(payload);

      if (res?.success) {
        fetchData();
        setOtpSend("");
        setOTP("");
      }
    } catch (e) {
    }
  };

  const handleOtpSentFromChild = (channel: "Email" | "PhoneNo") => {
    setOtpSend(channel);
  };

  const AccountSettingsSkeleton = () => (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      {[...Array(4)].map((_, index) => (
        <div
          key={index}
          className="flex justify-between items-center mb-6 pb-6 border-b border-neutral-200 last:border-b-0 last:mb-0 last:pb-0"
        >
          <div className="flex-1">
            <div className="h-5 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/6 mt-2"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded w-24"></div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <RequirementCommonHeader title={"Account Settings"} />
      <ContentWithLoader loading={loader} content={<AccountSettingsSkeleton />}>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          {/* Email Section */}
          <div className="flex justify-between items-center mb-6 pb-6 border-b border-neutral-200">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                <FormattedMessage
                  id="CORPORATE.ACCOUNT.SETTINGS.EMAIL.TITLE"
                  defaultMessage="Email"
                />
              </h2>
              <div className="text-gray-600">{data?.corporate?.email}</div>
              {otpsend !== "Email" && data?.user?.isEmailVerified === true && (
                <div className="flex items-center text-green-600 text-sm mt-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                  Verified
                </div>
              )}
            </div>
            <div className="flex space-x-3 items-center">
              {otpsend === "Email" && (
                <div className="flex space-x-3 items-center">
                  <Input
                    onChange={(e) => setOTP(e.target.value)}
                    value={otp}
                    placeholder="Enter OTP"
                    InputDivClass="mt-0 mb-0"
                    inputClass="border border-gray-300 rounded-lg h-10 w-32"
                  />
                  <button
                    onClick={handleVerifyOtp}
                    className="h-10 flex items-center text-white bg-[#0078ae] font-bold rounded-lg text-sm px-4 cursor-pointer hover:bg-[#006a9a] transition-colors"
                  >
                    Submit
                  </button>
                </div>
              )}

              {otpsend !== "Email" && data?.user?.isEmailVerified !== true && (
                <button
                  onClick={() => handleOTP("email")}
                  className="h-10 flex items-center text-[#0078ae] font-bold hover:bg-[#EEF1FE] px-4 rounded-lg text-sm cursor-pointer border border-[#C7D2F6] transition-colors"
                >
                  <FormattedMessage
                    id="CORPORATE.ACCOUNT.SETTINGS.VERIFY_EMAIL.BUTTON"
                    defaultMessage="Verify email"
                  />
                </button>
              )}

              <button
                onClick={() => openModel(0)}
                className="h-10 flex items-center font-bold text-[#0078ae] hover:bg-[#EEF1FE] px-4 rounded-lg text-sm cursor-pointer border border-[#C7D2F6] transition-colors"
              >
                <FormattedMessage
                  id="CORPORATE.ACCOUNT.SETTINGS.CHANGE_EMAIL.BUTTON"
                  defaultMessage="Change email"
                />
              </button>
            </div>
          </div>

          {/* Password Section */}
          <div className="flex justify-between items-center mb-6 pb-6 border-b border-neutral-200">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800">
                <FormattedMessage
                  id="CORPORATE.ACCOUNT.SETTINGS.PASSWORD.TITLE"
                  defaultMessage="Password"
                />
              </h2>
            </div>
            <button
              onClick={() => openModel(1)}
              className="h-10 flex items-center text-[#0078ae] font-bold hover:bg-[#EEF1FE] px-4 rounded-lg text-sm cursor-pointer border border-[#C7D2F6] transition-colors"
            >
              <FormattedMessage
                id="CORPORATE.ACCOUNT.SETTINGS.CHANGE_PASSWORD.BUTTON"
                defaultMessage="Change password"
              />
            </button>
          </div>

          {/* Phone Section */}
          <div className="flex justify-between items-center mb-6 pb-6 border-b border-neutral-200">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                <FormattedMessage
                  id="CORPORATE.ACCOUNT.SETTINGS.PHONENO.TITLE"
                  defaultMessage="Phone number"
                />
              </h2>
              <div className="text-gray-600">{data?.corporate?.mobile_number}</div>
              {otpsend !== "PhoneNo" && data?.user?.isPhoneVerified === true && (
                <div className="flex items-center text-green-600 text-sm mt-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                  Verified
                </div>
              )}
            </div>
            <div className="flex space-x-3 items-center">
              {otpsend === "PhoneNo" && (
                <div className="flex space-x-3 items-center">
                  <Input
                    onChange={(e) => setOTP(e.target.value)}
                    value={otp}
                    placeholder="Enter OTP"
                    InputDivClass="mt-0 mb-0"
                    inputClass="border border-gray-300 rounded-lg h-10 w-32"
                  />
                  <button
                    onClick={handleVerifyOtp}
                    className="h-10 flex items-center text-white bg-[#0078ae] font-bold rounded-lg text-sm px-4 cursor-pointer hover:bg-[#006a9a] transition-colors"
                  >
                    Submit
                  </button>
                </div>
              )}

              {otpsend !== "PhoneNo" && data?.user?.isPhoneVerified !== true && (
                <button
                  onClick={() => handleOTP("sms")}
                  className="h-10 flex items-center text-[#0078ae] font-bold hover:bg-[#EEF1FE] px-4 rounded-lg text-sm cursor-pointer border border-[#C7D2F6] transition-colors"
                >
                  <FormattedMessage
                    id="CORPORATE.ACCOUNT.SETTINGS.VERIFY_PHONENO.BUTTON"
                    defaultMessage="Verify Phone"
                  />
                </button>
              )}

              <button
                onClick={() => openModel(2)}
                className="h-10 flex items-center text-[#0078ae] font-bold hover:bg-[#EEF1FE] px-4 rounded-lg text-sm cursor-pointer border border-[#C7D2F6] transition-colors"
              >
                <FormattedMessage
                  id="CORPORATE.ACCOUNT.SETTINGS.CHANGE_PHONENO.BUTTON"
                  defaultMessage="Change Phone"
                />
              </button>
            </div>
          </div>

          {/* Sign Out */}
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-800">
                <FormattedMessage
                  id="CORPORATE.ACCOUNT.SETTINGS.SIGN_OUT.TITLE"
                  defaultMessage="Sign Out"
                />
              </h2>
              <div className="text-gray-600">{data?.corporate?.email}</div>
            </div>
            <button
              onClick={logout}
              className="h-10 flex items-center text-red-600 font-medium hover:bg-red-50 px-4 rounded-lg text-sm cursor-pointer border border-red-200 transition-colors"
            >
              <FormattedMessage
                id="CORPORATE.ACCOUNT.SETTINGS.SIGN_OUT.BUTTON"
                defaultMessage="Sign Out"
              />
            </button>
          </div>
        </div>

        {/* Modal */}
        <ModelWrapperWithButton
          Save={HandleChange}
          showModal={showModal}
          onCheckModel={HandleModel}
          headertitle={activeComponent?.headertitle}
        >
          {activeComponent?.componentName && (
            <activeComponent.componentName
              ref={childRef}
              HandleModel={HandleModel}
              onOtpSent={handleOtpSentFromChild}
            />
          )}
        </ModelWrapperWithButton>
      </ContentWithLoader>
    </>

  );
};
